import Maybe from 'true-myth/maybe';
import Result from 'true-myth/result';
import { fromResult } from 'true-myth/toolbelt';

import { competenciesSchema, jobProfilesOptionsResponseSchema } from '../../job-profile/job-profile-schema';
import { jobLevelDetailsSchema } from '../../position-management/job-level-details';

import type { ValidatingHttpFetcherWithBaseUrl } from '../../../../shared/source/validating-http-fetcher';
import type { LoaderFunction } from 'react-router';

const fetchJobLevelDescriptionSections = async (
    validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl,
): Promise<Maybe<readonly string[]>> => {
    const result = await validatingCobrainerApiFetcher.get(jobProfilesOptionsResponseSchema, {
        pathname: `/job-profiles/options`,
    });

    if (result.isErr) {
        return Maybe.nothing();
    }

    const data = fromResult(result);

    return data.map((options) => options.structuredTags);
};

export const createJobLevelPageLoader = (
    validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl,
): LoaderFunction => {
    return async (loaderArguments) => {
        const jobArchitectureId = Maybe.of(loaderArguments.params['jobArchitectureId']);
        const jobLevelId = Maybe.of(loaderArguments.params['jobLevelId']);
        const mode = Maybe.of(loaderArguments.params['mode']);

        if (jobLevelId.isNothing || jobArchitectureId.isNothing) {
            return Result.err(new Error('No jobArchitectureId/jobLevelId path parameter present'));
        }

        const [jobLevel, competencies, structuredSections] = await Promise.all([
            validatingCobrainerApiFetcher.get(jobLevelDetailsSchema, {
                pathname: `/job-architectures/${jobArchitectureId.value}/levels/${jobLevelId.value}`,
            }),
            validatingCobrainerApiFetcher.get(competenciesSchema, {
                pathname: '/skills/competencies',
            }),
            fetchJobLevelDescriptionSections(validatingCobrainerApiFetcher),
        ]);

        const isInEditMode =
            mode.equals(Maybe.of('edit')) && jobLevel.map((level) => level.actions?.canEdit).unwrapOr(false);

        return Result.ok({
            competencies: fromResult(competencies),
            isInEditMode,
            jobLevel: fromResult(jobLevel),
            structuredSections,
        });
    };
};
